import React, { useState, useEffect, useCallback, type MutableRefObject } from 'react';
import { Button, Spinner } from "@aidkitorg/component-library";
import { ChangeSet, reconstructStateFromEvents, State } from "@aidkitorg/typesheets/lib/collab";
import { ChangeList, type ChangesToRenderMap, buildChangesToRender } from "./Changes";

type PublishedChangesState = {
  loading: boolean;
  error?: string;
  changes: ChangesToRenderMap;
  combinedState?: State;
  parentState?: State;
  parentVersionId?: string | null;
  publishedVersionId?: string;
};

type PublishedChangesResponse = {
  error: string;
} | {
  eventsAdded: ChangeSet;
  eventsRemoved: ChangeSet;
  parentVersionId: string;
  publishedVersionId: string;
};

type PublishedChangesModalProps = {
  open: boolean;
  onClose: () => void;
  fetchPublishedChanges: () => Promise<PublishedChangesResponse | undefined>;
  branchId: number | undefined;
  parentStateRef?: MutableRefObject<State | undefined>;
  parentOpsRef?: MutableRefObject<ChangeSet>;
};

export function PublishedChangesModal({
  open,
  onClose,
  fetchPublishedChanges,
  branchId,
  parentStateRef,
  parentOpsRef,
}: PublishedChangesModalProps) {
  const [state, setState] = useState<PublishedChangesState>({ loading: false, changes: {} });

  const load = useCallback(async () => {
    if (!branchId) return;
    setState(prev => ({ ...prev, loading: true, error: undefined }));
    try {
      const response = await fetchPublishedChanges();
      if (!response || 'error' in response) {
        setState({
          loading: false,
          changes: {},
          combinedState: undefined,
          error: response && 'error' in response ? response.error : 'Failed to load published changes',
        });
        return;
      }
      const { eventsAdded, eventsRemoved, parentVersionId, publishedVersionId } = response;

      const parentOps = parentOpsRef?.current ?? [];
      const parentState = parentStateRef?.current;
      const [, publishedState] = reconstructStateFromEvents([...parentOps, ...eventsAdded]);
      const { changes } = buildChangesToRender(publishedState, eventsAdded, parentState, eventsRemoved);
      setState({
        loading: false,
        changes,
        combinedState: publishedState,
        parentState,
        parentVersionId,
        publishedVersionId,
      });
    } catch (e) {
      setState({ loading: false, changes: {}, error: e instanceof Error ? e.message : 'Failed to load published changes' });
    }
  }, [branchId, fetchPublishedChanges]);

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const hasChanges = Object.keys(state.changes).length > 0;

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 flex flex-col p-4">
      <div className="bg-white rounded-lg shadow-lg w-full h-full flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h2 className="text-lg font-semibold">Published changes since last sync</h2>
            <p className="text-xs text-gray-500">
              Published: {state.publishedVersionId ? state.publishedVersionId.slice(0, 8) : 'latest'} ·
              {' '}Branch base: {state.parentVersionId ? state.parentVersionId.slice(0, 8) : 'unknown'}
            </p>
          </div>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
        <div className="px-4 py-3 border-b">
          <p className="text-sm text-gray-600">
            These are the changes that have been published since your branch last synced.
            Review them to ensure your changes are compatible.
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {state.loading && (
            <div className="flex items-center justify-center h-full">
              <Spinner />
            </div>
          )}
          {!state.loading && state.error && (
            <div className="space-y-4">
              <p className="text-sm text-red-600">{state.error}</p>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={load}>Retry</Button>
              </div>
            </div>
          )}
          {!state.loading && !state.error && state.combinedState && hasChanges && (
            <ChangeList state={state.combinedState} changes={state.changes} parentState={state.parentState} />
          )}
          {!state.loading && !state.error && state.combinedState && !hasChanges && (
            <p className="text-sm text-gray-600">Published hasn&apos;t changed since your last sync.</p>
          )}
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <Button variant="secondary" onClick={load} isDisabled={state.loading}>
            Refresh
          </Button>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
